// AuxiliaryDialog — vista del libro auxiliar contable por animal (Sprint 2).
//
// Se monta desde PlanDeCuentasPage al hacer click en "Ver auxiliar" sobre una
// cuenta con has_auxiliary=true.
//
// Read-only en V1 (las entries se persisten al sellar; no se editan manuales).
// Filtros: animal, rango de fechas, export.

import { DatePicker } from "@/common/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAnimalsList } from "@/features/animals/api/animalsApi";
import { Filter, X } from "lucide-react";
import { useMemo, useState } from "react";
import { type AuxiliaryFilters, useAuxiliaryEntries } from "../api/auxiliaryApi";
import type { ChartNode } from "../api/chartApi";

type Props = {
  accountId: string;
  chartAccount: ChartNode;
  onClose: () => void;
};

function formatCRC(cents: number): string {
  // Determinista, sin Intl (i18n consistente en toda la app).
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const int = Math.floor(abs / 100);
  const frac = abs % 100;
  const intStr = int.toLocaleString("es-CR");
  return `${sign}₡ ${intStr},${String(frac).padStart(2, "0")}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("es-CR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "America/Costa_Rica",
  });
}

function toIsoDate(d: Date | undefined): string | undefined {
  if (!d) return undefined;
  return d.toISOString().slice(0, 10);
}

export function AuxiliaryDialog({ accountId, chartAccount, onClose }: Props) {
  const [animalId, setAnimalId] = useState<string | undefined>(undefined);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);

  const filters: AuxiliaryFilters = useMemo(
    () => ({
      animalId,
      fromDate: toIsoDate(fromDate),
      toDate: toIsoDate(toDate),
      limit: 200,
    }),
    [animalId, fromDate, toDate],
  );

  const animalsQuery = useAnimalsList(accountId);
  const auxiliaryQuery = useAuxiliaryEntries(accountId, chartAccount.id, filters);

  const animals = animalsQuery.data?.animals ?? [];
  const entries = auxiliaryQuery.data?.entries ?? [];
  const totals = auxiliaryQuery.data?.totals ?? { debitCents: 0, creditCents: 0 };
  const saldoCents = totals.debitCents - totals.creditCents;

  const hasFilters = animalId || fromDate || toDate;

  function resetFilters() {
    setAnimalId(undefined);
    setFromDate(undefined);
    setToDate(undefined);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 sm:items-center sm:px-4 sm:py-8"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="presentation"
    >
      <div
        className="flex h-full w-full max-w-5xl flex-col overflow-hidden bg-app-surface shadow-xl sm:h-auto sm:max-h-[90vh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        // biome-ignore lint/a11y/useSemanticElements: misma decisión que DialogShell en PlanDeCuentasPage — el <dialog> HTML5 no se integra con mount/unmount condicional.
        role="dialog"
        aria-modal="true"
        aria-labelledby="auxiliary-dialog-title"
      >
        {/* Header */}
        <header className="flex items-start justify-between border-app-border border-b px-5 py-4">
          <div>
            <h2
              id="auxiliary-dialog-title"
              className="font-semibold text-app-ink-1 text-base sm:text-lg"
            >
              Auxiliar contable · {chartAccount.code} {chartAccount.name}
            </h2>
            <p className="mt-0.5 text-app-ink-3 text-xs sm:text-sm">
              Detalle por animal de los asientos sellados. Se actualiza al cerrar cada período.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-app-ink-3 hover:bg-app-surface-2"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Resumen */}
        <div className="grid grid-cols-1 gap-2 px-5 py-3 sm:grid-cols-3">
          <SummaryCard label="Débitos" value={formatCRC(totals.debitCents)} />
          <SummaryCard label="Créditos" value={formatCRC(totals.creditCents)} />
          <SummaryCard label="Saldo" value={formatCRC(saldoCents)} bold />
        </div>

        {/* Filtros */}
        <div className="flex flex-col gap-2 border-app-border border-t border-b bg-app-surface-2 px-5 py-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label htmlFor="aux-animal" className="mb-1 block font-medium text-app-ink-2 text-xs">
              Animal
            </label>
            <Select
              value={animalId ?? "__all__"}
              onValueChange={(v) => setAnimalId(v === "__all__" ? undefined : v)}
            >
              <SelectTrigger id="aux-animal" className="text-base">
                <SelectValue placeholder="Todos los animales" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los animales</SelectItem>
                {animals.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.tag}
                    {a.name ? ` · ${a.name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <span className="mb-1 block font-medium text-app-ink-2 text-xs">Desde</span>
            <DatePicker
              value={fromDate}
              onChange={setFromDate}
              placeholder="Sin límite"
              aria-label="Fecha desde"
            />
          </div>
          <div className="flex-1">
            <span className="mb-1 block font-medium text-app-ink-2 text-xs">Hasta</span>
            <DatePicker
              value={toDate}
              onChange={setToDate}
              placeholder="Sin límite"
              aria-label="Fecha hasta"
            />
          </div>
          {hasFilters && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 items-center gap-1.5 rounded-md border border-app-border bg-app-surface px-3 text-app-ink-2 text-sm hover:bg-app-surface"
            >
              <Filter className="h-3.5 w-3.5" />
              Limpiar
            </button>
          )}
        </div>

        {/* Tabla */}
        <div className="flex-1 overflow-auto">
          {auxiliaryQuery.isLoading ? (
            <div className="p-5">
              <div className="h-6 animate-pulse rounded bg-app-surface-2" />
            </div>
          ) : auxiliaryQuery.isError ? (
            <p className="p-5 text-app-ink-3 text-sm">
              No se pudo cargar el auxiliar. Reintentá en unos segundos.
            </p>
          ) : entries.length === 0 ? (
            <p className="p-5 text-app-ink-3 text-sm">
              {hasFilters
                ? "No hay entries del auxiliar con los filtros aplicados."
                : "Esta cuenta aún no tiene entries del auxiliar. Se generan al sellar un período."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-app-surface-2 text-app-ink-3 text-xs">
                  <tr>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-left">Animal</th>
                    <th className="px-4 py-2 text-right">Débito</th>
                    <th className="px-4 py-2 text-right">Crédito</th>
                    <th className="px-4 py-2 text-left">Descripción</th>
                    <th className="px-4 py-2 text-left">Export</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-app-border border-t">
                      <td className="whitespace-nowrap px-4 py-2 font-mono text-app-ink-2 text-xs">
                        {formatDate(e.occurredAt)}
                      </td>
                      <td className="px-4 py-2 text-app-ink-1">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="rounded-full bg-app-surface-2 px-2 py-0.5 font-mono text-[11px] text-app-ink-2">
                            {e.animalTag}
                          </span>
                          {e.animalName && (
                            <span className="text-app-ink-2 text-xs">{e.animalName}</span>
                          )}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-app-ink-1 text-xs">
                        {e.debitCents > 0 ? formatCRC(e.debitCents) : ""}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-app-ink-1 text-xs">
                        {e.creditCents > 0 ? formatCRC(e.creditCents) : ""}
                      </td>
                      <td className="px-4 py-2 text-app-ink-2 text-xs">{e.description}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-app-ink-3 text-xs">
                        <span
                          title={`Export ${e.journalExportPeriodStart} → ${e.journalExportPeriodEnd}`}
                        >
                          {e.journalExportPeriodStart.slice(0, 7)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer informativo */}
        <footer className="border-app-border border-t px-5 py-2 text-app-ink-3 text-xs">
          {entries.length > 0 ? (
            <>
              Mostrando {entries.length} {entries.length === 1 ? "entry" : "entries"}.
              {entries.length >= 200 && " Aplicá filtros para acotar el resultado."}
            </>
          ) : null}
        </footer>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="rounded-xl border border-app-border bg-app-surface px-3 py-2">
      <div className="text-app-ink-3 text-xs">{label}</div>
      <div
        className={`mt-0.5 font-mono text-app-ink-1 text-sm tabular-nums sm:text-base ${
          bold ? "font-semibold" : "font-normal"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
